import { useQuery } from "@tanstack/react-query";
import {
	CircleAlert,
	CircleCheck,
	Clock3,
	FileText,
	LoaderCircle,
	TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { api, type DaePaymentList } from "@/client";
import { MetricCard } from "@/components/MetricCard";
import { FinancePresentation } from "@/presentation/finance";

type DaePayment = DaePaymentList["payments"][number];
type DaePaymentStatus = DaePayment["status"];
type StatusFilter = "ALL" | DaePaymentStatus;

const statusPresentation: Record<
	DaePaymentStatus,
	{ label: string; className: string; icon: React.ReactNode }
> = {
	MATCHED: {
		label: "Encontrado no banco",
		className: "border-emerald-200 bg-emerald-50 text-emerald-800",
		icon: <CircleCheck className="size-3.5" />,
	},
	AWAITING_BANK: {
		label: "Aguardando atualização bancária",
		className: "border-amber-200 bg-amber-50 text-amber-800",
		icon: <Clock3 className="size-3.5" />,
	},
	REVIEW_REQUIRED: {
		label: "Revisar correspondência",
		className: "border-red-200 bg-red-50 text-red-800",
		icon: <TriangleAlert className="size-3.5" />,
	},
};

const statusFilters: DaePaymentStatus[] = ["MATCHED", "AWAITING_BANK", "REVIEW_REQUIRED"];

export function DaePaymentsView() {
	const [status, setStatus] = useState<StatusFilter>("ALL");
	const query = useQuery({
		queryKey: ["dae-payments", "current-month"],
		queryFn: () => api.daePayments.list(),
	});

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center rounded-3xl border border-stone-200 bg-white text-stone-600 shadow-sm">
				<div className="flex items-center gap-3 text-sm font-medium">
					<LoaderCircle className="size-5 animate-spin" />
					Cruzando DAE/MG com o banco…
				</div>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center shadow-sm">
				<CircleAlert className="mx-auto mb-3 size-8 text-red-600" />
				<h2 className="font-semibold text-stone-950">Não foi possível consultar os DAE/MG</h2>
				<p className="mt-2 text-sm text-stone-600">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;
	const visiblePayments = data.payments.filter(
		(payment) => status === "ALL" || payment.status === status,
	);

	return (
		<>
			<section className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
				<MetricCard
					label="DAE/MG pagos"
					value={FinancePresentation.money(data.summary.total.amountCents)}
					detail={`${data.summary.total.count} guias no mês`}
				/>
				<MetricCard
					label="Encontrados no banco"
					value={FinancePresentation.money(data.summary.matched.amountCents)}
					detail={`${data.summary.matched.count} correspondências únicas`}
					tone="success"
				/>
				<MetricCard
					label="Aguardando banco"
					value={FinancePresentation.money(data.summary.awaitingBank.amountCents)}
					detail={`${data.summary.awaitingBank.count} guias ainda não encontradas`}
					tone={data.summary.awaitingBank.count > 0 ? "attention" : "success"}
				/>
				<MetricCard
					label="Revisar correspondência"
					value={String(data.summary.reviewRequired.count)}
					detail={FinancePresentation.money(data.summary.reviewRequired.amountCents)}
					tone={data.summary.reviewRequired.count > 0 ? "attention" : "success"}
				/>
			</section>

			<section className="mb-6 rounded-3xl border border-emerald-900/10 bg-emerald-950 p-6 text-white shadow-sm">
				<div className="flex items-start gap-4">
					<span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white/10">
						<FileText className="size-5" />
					</span>
					<div>
						<h2 className="font-semibold">Documento primeiro, banco depois</h2>
						<p className="mt-1 max-w-4xl text-sm leading-6 text-emerald-50/75">
							O DAE SINDI informa quais guias foram pagas. Esta tela apenas lê esses dados e procura
							uma saída única na conta 58000 com a mesma data e o mesmo valor. Um vínculo único
							recebe automaticamente a classificação financeira GNRE; isso ainda não declara
							conciliação.
						</p>
						{data.coverage?.finishedAt && (
							<p className="mt-3 text-xs font-medium text-emerald-100/60">
								Banco atualizado {FinancePresentation.moment(data.coverage.finishedAt)}
								{data.coverage.to
									? ` · movimentos até ${FinancePresentation.transactionDate(data.coverage.to)}`
									: ""}
							</p>
						)}
					</div>
				</div>
			</section>

			<section className="overflow-hidden rounded-3xl border border-stone-200 bg-white shadow-sm">
				<header className="flex flex-col justify-between gap-4 border-b border-stone-200 px-5 py-5 sm:flex-row sm:items-center">
					<div>
						<h2 className="font-semibold text-stone-950">Guias do mês</h2>
						<p className="mt-1 text-xs text-stone-500">
							{visiblePayments.length} de {data.payments.length} guias exibidas
						</p>
					</div>
					<div className="flex flex-wrap gap-1.5">
						<FilterButton active={status === "ALL"} onClick={() => setStatus("ALL")}>
							Todas
						</FilterButton>
						{statusFilters.map((value) => (
							<FilterButton key={value} active={status === value} onClick={() => setStatus(value)}>
								{statusPresentation[value].label}
							</FilterButton>
						))}
					</div>
				</header>

				<div className="divide-y divide-stone-100">
					{visiblePayments.map((payment) => (
						<DaePaymentRow key={payment.accessKey} payment={payment} />
					))}
					{visiblePayments.length === 0 && (
						<p className="px-6 py-12 text-center text-sm text-stone-500">
							Nenhuma guia neste estado.
						</p>
					)}
				</div>
			</section>
		</>
	);
}

function DaePaymentRow({ payment }: { payment: DaePayment }) {
	const presentation = statusPresentation[payment.status];

	return (
		<article className="grid gap-4 px-5 py-5 transition hover:bg-stone-50/70 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_auto] lg:items-center">
			<div className="min-w-0">
				<div className="flex flex-wrap items-center gap-2">
					<span
						className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${presentation.className}`}
					>
						{presentation.icon}
						{presentation.label}
					</span>
					<span className="text-xs font-medium text-stone-500">
						{FinancePresentation.transactionDate(payment.paidAt.slice(0, 10))}
					</span>
				</div>
				<h3 className="mt-3 truncate font-semibold text-stone-950">{payment.issuerName}</h3>
				<p className="mt-1 text-xs text-stone-500">
					DAE {payment.documentNumber} · NF-e {payment.nfeNumber}
				</p>
			</div>

			<div className="min-w-0 text-sm">
				{payment.bankTransaction ? (
					<>
						<p className="truncate font-medium text-stone-800">
							{payment.bankTransaction.description}
						</p>
						<p className="mt-1 truncate text-xs text-stone-500">
							{payment.bankTransaction.receiverName ?? "Favorecido não informado pelo banco"}
						</p>
					</>
				) : payment.status === "REVIEW_REQUIRED" ? (
					<p className="text-sm text-red-700">
						{payment.reviewReason === "SHARED_BANK_TRANSACTION"
							? "O mesmo movimento também corresponde a outro DAE"
							: `${payment.candidateCount} movimentos bancários possíveis`}
					</p>
				) : (
					<p className="text-sm text-stone-500">Ainda não apareceu no extrato sincronizado</p>
				)}
			</div>

			<p className="text-xl font-semibold tracking-[-0.03em] text-stone-950 lg:text-right">
				{FinancePresentation.money(payment.totalCents)}
			</p>
		</article>
	);
}

function FilterButton({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200 hover:text-stone-900"}`}
		>
			{children}
		</button>
	);
}
